<div class="tab-body mat-elevation-z1">
  <div class="section-header">
    <h2>
      <mat-icon>people</mat-icon>
      Manage Users
    </h2>
  </div>

  <p class="users-hint">
    This is a list of users who have created an account on Teradata Trial. You can manage user roles, edit profiles, set credit limits, and suspend or re-enable accounts from here.
  </p>

  <div class="user-filter-row">
    <mat-form-field class="user-filter-field" appearance="outline">
      <mat-label>Filter By</mat-label>
      <mat-select [(ngModel)]="usersFilterField" (ngModelChange)="onUsersFilterFieldChange()">
        <mat-option *ngFor="let option of userFilterOptions" [value]="option.value">
          {{ option.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="user-filter-value" appearance="outline" *ngIf="!usesSelectableUserFilter()">
      <mat-label>Value</mat-label>
      <input matInput [(ngModel)]="usersFilterValue" (ngModelChange)="onUsersFilterValueChange()" (keyup.enter)="applyUsersFilterImmediately()" [placeholder]="getUserFilterPlaceholder()">
      <button mat-icon-button matSuffix *ngIf="usersFilterValue" type="button" (click)="usersFilterValue = ''; onUsersFilterValueChange()" aria-label="Clear filter value">
        <mat-icon>close</mat-icon>
      </button>
    </mat-form-field>

    <mat-form-field class="user-filter-value" appearance="outline" *ngIf="usesSelectableUserFilter()">
      <mat-label>Value</mat-label>
      <mat-select [(ngModel)]="usersFilterValue" (ngModelChange)="onUsersFilterValueChange()">
        <mat-option *ngFor="let option of userFilterValueOptions" [value]="option.value">
          {{ option.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <div class="user-filter-actions">
      <button mat-button type="button" (click)="clearUsersFilter()" [disabled]="loading || (!usersFilterValue && usersFilterField === 'email')">Clear</button>
    </div>
  </div>

  <div class="loading-overlay" *ngIf="loading">
    <mat-spinner></mat-spinner>
  </div>

  <table class="admin-table users-table" *ngIf="getDisplayedUsers().length > 0 && !loading">
    <thead>
      <tr>
        <th class="col-id">ID</th>
        <th class="col-email">Email</th>
        <th class="col-name">Name</th>
        <th class="col-company">Company</th>
        <th class="col-role">Role</th>
        <th class="col-status">Status</th>
        <th class="col-profile-type">Profile Type</th>
        <th class="col-credits">Credits Used</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let user of getDisplayedUsers()"
        class="user-row"
        tabindex="0"
        (click)="openUserInfo(user)"
        (keydown.enter)="openUserInfo(user)"
        (keydown.space)="openUserInfo(user); $event.preventDefault()">
        <td>{{ user.id }}</td>
        <td>{{ user.email }}</td>
        <td>{{ getUserDisplayName(user) }}</td>
        <td>{{ user.company || '—' }}</td>
        <td>
          <span class="user-role-badge" [class.user-role-admin]="isUserAdmin(user)">
            {{ isUserAdmin(user) ? 'Admin' : (user.role || 'User') }}
          </span>
        </td>
        <td>
          <span class="user-status-badge" [class.user-status-disabled]="user.disabled" [class.user-status-active]="!user.disabled">
            {{ user.disabled ? 'Suspended' : 'Active' }}
          </span>
        </td>
        <td>{{ getProfileTypeId(user) }}</td>
        <td>
          {{ getUserCredits(user) | number:'1.0-2' }}<ng-container *ngIf="getUserMaxCredits(user) !== null"> / {{ getUserMaxCredits(user) | number:'1.0-2' }}</ng-container>
        </td>
      </tr>
    </tbody>
  </table>

  <div class="empty-state" *ngIf="getDisplayedUsers().length === 0 && !loading">
    <mat-icon>person_off</mat-icon>
    <p>No users found.</p>
  </div>

  <div class="pagination-row" *ngIf="usersTotalItems > 0 && !loading">
    <button mat-stroked-button type="button" (click)="refreshUsers()">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>
    <button mat-stroked-button type="button" (click)="goToPreviousUsersPage()" [disabled]="usersPage === 1">Previous</button>
    <span>Page {{ usersPage }} of {{ usersTotalPages }}</span>
    <button mat-stroked-button type="button" (click)="goToNextUsersPage()" [disabled]="usersPage >= usersTotalPages">Next</button>
  </div>
</div>

<div class="modal-backdrop" *ngIf="selectedUser">
  <div class="modal-card mat-elevation-z8 user-info-card">
    <div class="modal-header">
      <h3>
        <mat-icon>person</mat-icon>
        {{ selectedUser!.id }}
      </h3>
      <button mat-icon-button type="button" (click)="closeUserInfo()" aria-label="Close user details">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="modal-body">
      <div class="user-info-status">
        <span class="user-role-badge" [class.user-role-admin]="isUserAdmin(selectedUser!)">
          {{ isUserAdmin(selectedUser!) ? 'Admin' : (selectedUser!.role || 'User') }}
        </span>
        <span class="user-status-badge" [class.user-status-disabled]="selectedUser!.disabled" [class.user-status-active]="!selectedUser!.disabled">
          {{ selectedUser!.disabled ? 'Suspended' : 'Active' }}
        </span>
      </div>

      <h4 class="user-info-subtitle">Profile</h4>
      <div class="form-row material-grid">
        <mat-form-field appearance="outline">
          <mat-label>First Name</mat-label>
          <input matInput [(ngModel)]="userForm.firstname" placeholder="">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Last Name</mat-label>
          <input matInput [(ngModel)]="userForm.lastname" placeholder="">
        </mat-form-field>
      </div>
      <div class="form-row material-grid">
        <mat-form-field appearance="outline">
          <mat-label>Email</mat-label>
          <input matInput [value]="selectedUser!.email" disabled>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Country</mat-label>
          <input matInput [(ngModel)]="userForm.country" placeholder="">
        </mat-form-field>
      </div>
      <div class="form-row material-grid">
        <mat-form-field appearance="outline">
          <mat-label>Company Name</mat-label>
          <input matInput [(ngModel)]="userForm.company" placeholder="">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Job Title</mat-label>
          <input matInput [(ngModel)]="userForm.title" placeholder="">
        </mat-form-field>
      </div>

      <h4 class="user-info-subtitle">User History</h4>
      <div class="user-history-grid">
        <div class="user-history-card">
          <div class="user-history-label"><mat-icon>paid</mat-icon> Cost (Credits Used)</div>
          <div class="user-history-value">{{ getUserCredits(selectedUser!) | number:'1.0-2' }}</div>
        </div>
        <div class="user-history-card">
          <div class="user-history-label"><mat-icon>badge</mat-icon> Profile Type</div>
          <div class="user-history-value">
            {{ getProfileTypeId(selectedUser!) }}<ng-container *ngIf="getUserMaxCredits(selectedUser!) !== null"> ({{ getUserMaxCredits(selectedUser!) | number:'1.0-2' }} credits)</ng-container>
          </div>
          <button mat-stroked-button color="primary" type="button" class="user-history-action"
            (click)="openProfileType(selectedUser!)"
            [disabled]="isActionBusy(userProfileActionKey(selectedUser!.id))">
            <mat-icon>badge</mat-icon> Change Profile Type
          </button>
        </div>
        <div class="user-history-card user-notebooks-card">
          <div class="user-history-label"><mat-icon>menu_book</mat-icon> Notebooks Used</div>

          <div class="user-notebooks-loading" *ngIf="notebooksLoading">
            <mat-spinner diameter="18"></mat-spinner>
            <span>Loading notebook activity…</span>
          </div>

          <p class="user-history-empty" *ngIf="!notebooksLoading && notebooksError">{{ notebooksError }}</p>

          <p class="user-history-empty" *ngIf="!notebooksLoading && !notebooksError && userNotebooks.length === 0">
            No environments found for this user.
          </p>

          <div class="user-env-list" *ngIf="!notebooksLoading && !notebooksError && userNotebooks.length > 0">
            <div class="user-env-item" *ngFor="let env of userNotebooks">
              <div class="user-env-header">
                <button type="button" class="user-env-name user-env-name-link"
                  (click)="viewUserEnvironment(env)"
                  matTooltip="View environment details">
                  <mat-icon>dns</mat-icon> {{ env.environment }}
                </button>
                <span class="user-env-state" [class.running]="env.state === 'RUNNING'">{{ env.state }}</span>
                <a *ngIf="env.jupyterUrl" mat-stroked-button color="primary" class="user-env-open"
                  [href]="env.jupyterUrl" target="_blank" rel="noopener noreferrer"
                  matTooltip="Open this user's JupyterLab environment as an admin">
                  <mat-icon>open_in_new</mat-icon>
                  Open
                </a>
                <span *ngIf="!env.jupyterUrl" matTooltip="Access link is only available while the environment is running">
                  <button mat-stroked-button type="button" class="user-env-open" disabled>
                    <mat-icon>open_in_new</mat-icon>
                    Open
                  </button>
                </span>
              </div>

              <p class="user-env-message" *ngIf="env.message">{{ env.message }}</p>

              <table class="user-notebooks-table" *ngIf="env.notebooks.length > 0">
                <thead>
                  <tr>
                    <th class="nb-col-name">Notebook</th>
                    <th class="nb-col-count">Opens</th>
                    <th class="nb-col-time">Last Opened</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let nb of env.notebooks">
                    <td class="nb-col-name">{{ nb.name }}</td>
                    <td class="nb-col-count">{{ nb.openCount }}</td>
                    <td class="nb-col-time">{{ nb.lastOpened }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="user-history-card user-usage-card">
          <div class="user-history-label"><mat-icon>insights</mat-icon> Usage History (Current Billing Period)</div>

          <div class="user-notebooks-loading" *ngIf="usageLoading">
            <mat-spinner diameter="18"></mat-spinner>
            <span>Loading usage history…</span>
          </div>

          <p class="user-history-empty" *ngIf="!usageLoading && usageError">{{ usageError }}</p>

          <ng-container *ngIf="!usageLoading && !usageError && userUsage">
            <div class="user-usage-summary">
              <div class="user-usage-summary-item">
                <span class="user-usage-summary-value">{{ userUsage.totalUsageHours | number:'1.0-2' }}</span>
                <span class="user-usage-summary-label">Hours Used</span>
              </div>
              <div class="user-usage-summary-item">
                <span class="user-usage-summary-value">{{ getUserCredits(selectedUser!) | number:'1.0-2' }}</span>
                <span class="user-usage-summary-label">Credits Used</span>
              </div>
              <div class="user-usage-summary-item" *ngIf="userUsage.budgetCredits !== null">
                <span class="user-usage-summary-value">{{ userUsage.budgetCredits | number:'1.0-2' }}</span>
                <span class="user-usage-summary-label">Budget Credits</span>
              </div>
              <div class="user-usage-summary-item" *ngIf="userUsage.billingMonthBegin">
                <span class="user-usage-summary-value">{{ userUsage.billingMonthBegin }}</span>
                <span class="user-usage-summary-label">Period Start</span>
              </div>
            </div>

            <p class="user-history-empty" *ngIf="userUsage.records.length === 0">
              No usage has been recorded for the current billing period.
            </p>

            <table class="user-notebooks-table" *ngIf="userUsage.records.length > 0">
              <thead>
                <tr>
                  <th class="nb-col-name">Machine</th>
                  <th class="nb-col-time">Date</th>
                  <th class="nb-col-count">Hours</th>
                  <th class="nb-col-count">CPUs</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let record of userUsage.records" [class.user-usage-deleted]="record.deleted">
                  <td class="nb-col-name">{{ record.machineName }}</td>
                  <td class="nb-col-time">{{ record.activityDate }}</td>
                  <td class="nb-col-count">{{ record.usageHours | number:'1.0-2' }}</td>
                  <td class="nb-col-count">{{ record.cpuCount }}</td>
                </tr>
              </tbody>
            </table>
          </ng-container>
        </div>
      </div>
    </div>

    <div class="modal-footer user-info-footer">
      <div class="user-info-footer-actions">
        <button mat-stroked-button color="primary" type="button"
          (click)="toggleAdmin(selectedUser!)"
          [disabled]="isActionBusy(userUpdateActionKey(selectedUser!.id))">
          <mat-spinner *ngIf="isActionBusy(userUpdateActionKey(selectedUser!.id))" diameter="16" class="btn-spinner"></mat-spinner>
          <mat-icon *ngIf="!isActionBusy(userUpdateActionKey(selectedUser!.id))">{{ isUserAdmin(selectedUser!) ? 'remove_moderator' : 'admin_panel_settings' }}</mat-icon>
          {{ isUserAdmin(selectedUser!) ? 'Remove Admin' : 'Make Admin' }}
        </button>
        <button mat-stroked-button color="warn" type="button" *ngIf="!selectedUser!.disabled"
          (click)="confirmTerminateUser(selectedUser!)"
          [disabled]="isActionBusy(userMaintenanceActionKey(selectedUser!.id))">
          <mat-spinner *ngIf="isActionBusy(userMaintenanceActionKey(selectedUser!.id))" diameter="16" class="btn-spinner"></mat-spinner>
          <mat-icon *ngIf="!isActionBusy(userMaintenanceActionKey(selectedUser!.id))">block</mat-icon>
          Suspend
        </button>
        <button mat-stroked-button color="accent" type="button" *ngIf="selectedUser!.disabled"
          (click)="reEnableUser(selectedUser!)"
          [disabled]="isActionBusy(userMaintenanceActionKey(selectedUser!.id))">
          <mat-spinner *ngIf="isActionBusy(userMaintenanceActionKey(selectedUser!.id))" diameter="16" class="btn-spinner"></mat-spinner>
          <mat-icon *ngIf="!isActionBusy(userMaintenanceActionKey(selectedUser!.id))">how_to_reg</mat-icon>
          Re-Enable
        </button>
      </div>
      <div class="user-info-footer-primary">
        <button mat-stroked-button type="button" (click)="closeUserInfo()">Close</button>
        <button mat-flat-button color="primary" type="button" (click)="saveUser()"
          [disabled]="isActionBusy(userUpdateActionKey(selectedUser!.id))">
          <mat-spinner *ngIf="isActionBusy(userUpdateActionKey(selectedUser!.id))" diameter="16" class="btn-spinner"></mat-spinner>
          <mat-icon *ngIf="!isActionBusy(userUpdateActionKey(selectedUser!.id))">save</mat-icon>
          Save Changes
        </button>
      </div>
    </div>
  </div>
</div>

<div class="modal-backdrop" *ngIf="terminateConfirmUser">
  <div class="modal-card mat-elevation-z8">
    <div class="modal-header">
      <h3>Confirm Suspend User</h3>
      <button mat-icon-button type="button" (click)="cancelTerminateUser()" aria-label="Close terminate user confirmation">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="modal-body">
      <p>
        Are you sure you want to <strong>suspend</strong>
        <code>{{ terminateConfirmUser.email }}</code>? Their account will be disabled.
        You can re-enable it later.
      </p>
    </div>
    <div class="modal-footer">
      <button mat-stroked-button type="button" (click)="cancelTerminateUser()">Cancel</button>
      <button mat-flat-button color="warn" type="button" (click)="terminateUser()">
        <mat-icon>block</mat-icon> Suspend User
      </button>
    </div>
  </div>
</div>

<div class="modal-backdrop" *ngIf="profileTypeUser">
  <div class="modal-card mat-elevation-z8">
    <div class="modal-header">
      <h3>Set Profile Type</h3>
      <button mat-icon-button type="button" (click)="cancelProfileType()" aria-label="Close profile type dialog">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="modal-body">
      <p>
        Set the profile type for <code>{{ profileTypeUser.email }}</code>.
        The profile type determines the user's monthly credit limit.
      </p>
      <mat-form-field appearance="outline" class="credit-limit-field">
        <mat-label>Profile Type</mat-label>
        <mat-select [(ngModel)]="profileTypeValue">
          <mat-option *ngFor="let option of profileTypeOptions" [value]="option.id">
            {{ option.id }} ({{ option.maxCredits | number:'1.0-2' }} credits)
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <div class="modal-footer">
      <button mat-stroked-button type="button" (click)="cancelProfileType()">Cancel</button>
      <button mat-flat-button color="primary" type="button" (click)="saveProfileType()"
        [disabled]="profileTypeUser && isActionBusy(userProfileActionKey(profileTypeUser.id))">
        <mat-icon>save</mat-icon> Save Profile Type
      </button>
    </div>
  </div>
</div>
